import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import { useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import i18n from "./i18n";
import { useTranslation } from "react-i18next";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const savedLang = localStorage.getItem("lang");
    if (savedLang && savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
  }, []);

  /*
   * Supabase elabora automaticamente il link di password recovery.
   * Non cerchiamo più access_token nell'hash: quando React monta,
   * il token può essere già stato consumato dall'Auth client e la
   * sessione recovery essere già disponibile.
   */
  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    const clearRetryTimer = () => {
      if (retryTimer) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }
    };

    const acceptSession = (session) => {
      if (cancelled || !session?.access_token) {
        return false;
      }

      clearRetryTimer();
      setError("");
      setLoading(false);
      return true;
    };

    const getCallbackError = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(
        window.location.hash.replace(/^#/, "")
      );

      return (
        searchParams.get("error_description") ||
        hashParams.get("error_description") ||
        searchParams.get("error") ||
        hashParams.get("error") ||
        ""
      );
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;

      if (event === "PASSWORD_RECOVERY") {
        acceptSession(session);
      }
    });

    const initializeRecovery = async () => {
      try {
        if (getCallbackError()) {
          setError(t("tokenNonValido") || "Token non valido o scaduto.");
          setLoading(false);
          return;
        }

        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (cancelled) return;

        if (sessionError) {
          setError(t("tokenNonValido") || "Token non valido o scaduto.");
          setLoading(false);
          return;
        }

        if (acceptSession(session)) {
          return;
        }

        retryTimer = window.setTimeout(async () => {
          try {
            const {
              data: { session: retrySession },
              error: retryError,
            } = await supabase.auth.getSession();

            if (cancelled) return;

            if (retryError || !acceptSession(retrySession)) {
              setError(
                t("tokenNonValido") || "Token non valido o scaduto."
              );
              setLoading(false);
            }
          } catch (retryException) {
            if (cancelled) return;

            console.error(
              "Errore verifica sessione password recovery:",
              retryException
            );
            setError(t("tokenNonValido") || "Token non valido o scaduto.");
            setLoading(false);
          }
        }, 2000);
      } catch (recoveryError) {
        if (cancelled) return;

        console.error(
          "Errore inizializzazione password recovery:",
          recoveryError
        );
        setError(t("tokenNonValido") || "Token non valido o scaduto.");
        setLoading(false);
      }
    };

    initializeRecovery();

    return () => {
      cancelled = true;
      clearRetryTimer();
      subscription.unsubscribe();
    };
  }, [t]);

  const handleReset = async (e) => {
    e.preventDefault();

    if (submitting) return;

    setError("");

    if (!password || !confirmPassword) {
      setError(t("compilaCampi2"));
      return;
    }

    if (password !== confirmPassword) {
      setError(t("passwordNonCoincidono"));
      return;
    }

    setSubmitting(true);

    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session?.access_token) {
        setError(t("tokenNonValido") || "Token non valido o scaduto.");
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password,
      });

      if (updateError) {
        setError(updateError.message);
        return;
      }

      setSuccessMessage(t("passwordAggiornata"));

      window.setTimeout(async () => {
        try {
          await supabase.auth.signOut({ scope: "local" });
        } catch (signOutError) {
          console.warn(
            "Password aggiornata, ma logout recovery non riuscito:",
            signOutError
          );
        } finally {
          navigate("/", { replace: true });
        }
      }, 2000);
    } catch (resetError) {
      console.error("Errore aggiornamento password:", resetError);
      setError(t("tokenNonValido") || "Token non valido o scaduto.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <p className="instructions">{t("caricamento")}</p>
      </div>
    );
  }

  return (
    <div className="container">
      <img src="/logo.png" alt="CarboZen Logo" className="logo-image" />

      <div
        className="card-section card-green auth-card"
        style={{ textAlign: "center" }}
      >
        <h2 className="titlesec">{t("reimpostaPasswordTitolo")}</h2>

        {successMessage ? (
          <p style={{ color: "green" }}>{successMessage}</p>
        ) : error ? (
          <>
            <p style={{ color: "red", marginTop: "0.5rem" }}>{error}</p>
            <p style={{ marginTop: "1rem" }}>
              <a href="/forgot-password" className="form-label">
                {t("passwordDimenticata") || "Richiedi un nuovo link"}
              </a>
            </p>
          </>
        ) : (
          <form onSubmit={handleReset}>
            <label className="form-label">
              <div className="input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder={t("nuovaPassword1")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="glicemia-input2"
                  style={{ paddingRight: "40px" }}
                  autoComplete="new-password"
                />
                <span
                  className="eye-icon"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </span>
              </div>
            </label>

            <label className="form-label">
              <div className="input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder={t("nuovaPassword2")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="glicemia-input2"
                  autoComplete="new-password"
                />
              </div>
            </label>

            <div className="wizard-buttons">
              <button
                type="submit"
                className="custom-button"
                disabled={submitting}
              >
                {submitting
                  ? t("salvataggio") || "Salvataggio..."
                  : t("reimpostaPasswordBottone")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
